import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import {
  useAvailability,
  useBooking,
  useCreateBooking,
  useRooms,
  useSaveParticipants,
  useSlots,
  useUpdateBooking,
} from '../api/queries'
import type { ParticipantRef, Slot, Suggestion } from '../api/types'
import type { BookingDoneHint } from './BookingDonePage'
import { ErrorNotice } from '../components/ErrorNotice'
import { ParticipantPicker, type PickedParticipant } from '../components/ParticipantPicker'
import { Badge, Button, Field, Input, Loading, PageTitle, Select } from '../components/ui'
import { cn } from '../lib/cn'
import { invited, meetings, people, seats } from '../lib/words'
import {
  atWallTime,
  dateKey,
  daysFromToday,
  DEFAULT_DURATION_MINUTES,
  fmtDate,
  fmtDuration,
  fmtRange,
  minutesBetween,
  pad,
  parse,
  SLOT_MINUTES,
  toIso,
  wallTimeOptions,
} from '../lib/time'
import { useCanBook, useSession } from '../session'

/**
 * Экран 2.4 «Новая бронь» — он же форма переноса (сценарий 6).
 *
 * Здесь сходятся почти все развилки, поэтому по порядку:
 *   2.7  — слот заняли, пока заполнялась форма: показываем альтернативы, а не ошибку;
 *   2.9  — позвать того, кого нет в системе: приглашение по адресу в домене;
 *   2.10 — правка брони из серии: одна встреча или вся серия, обязательный выбор;
 *   2.11 — конфликты в серии: пропустить занятые даты, создать остальные;
 *   2.14 — людей больше, чем мест: предупреждение и комнаты побольше.
 */

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const
const WEEKDAY_NAMES = [
  'воскресеньям',
  'понедельникам',
  'вторникам',
  'средам',
  'четвергам',
  'пятницам',
  'субботам',
]

export function BookingFormPage() {
  const { bookingId: bookingIdParam } = useParams()
  const editingId = bookingIdParam ? Number(bookingIdParam) : undefined
  const isEditing = editingId !== undefined && Number.isFinite(editingId)

  const [params] = useSearchParams()
  const { user, policy } = useSession()
  const canBook = useCanBook()
  const navigate = useNavigate()

  const rooms = useRooms()
  const existing = useBooking(isEditing ? editingId : undefined)

  // --- Значения формы -------------------------------------------------------
  const [roomId, setRoomId] = useState<number>(() => Number(params.get('room')) || 0)
  const [date, setDate] = useState(() => initialDate(params.get('start')))
  const [startTime, setStartTime] = useState(() => initialTime(params.get('start'), policy.work_start))
  const [duration, setDuration] = useState(() => initialDuration(params.get('start'), params.get('end')))
  const [title, setTitle] = useState('')
  const [participants, setParticipants] = useState<PickedParticipant[]>([])
  const [recurring, setRecurring] = useState(false)
  const [until, setUntil] = useState('')
  const [apply, setApply] = useState<'single' | 'series'>('single')
  const [skipConflicts, setSkipConflicts] = useState(false)

  // Подставляем значения правимой брони, когда она пришла.
  useEffect(() => {
    const booking = existing.data
    if (!booking) return
    setRoomId(booking.room_id)
    setDate(dateKey(parse(booking.starts_at)))
    setStartTime(hhmm(parse(booking.starts_at)))
    setDuration(minutesBetween(booking.starts_at, booking.ends_at))
    setTitle(booking.title ?? '')
    setParticipants(
      (booking.participants ?? [])
        .filter((participant) => participant.status === 'invited')
        .map((participant) => ({
          user_id: participant.user_id,
          email: participant.email,
          display_name: participant.display_name,
          position: participant.position,
        })),
    )
  }, [existing.data])

  const startsAt = useMemo(() => toIso(atWallTime(date, startTime)), [date, startTime])
  const endsAt = useMemo(
    () => toIso(new Date(atWallTime(date, startTime).getTime() + duration * 60_000)),
    [date, startTime, duration],
  )

  const room = rooms.data?.find((candidate) => candidate.id === roomId)

  // --- Занятость выбранной комнаты на выбранный день -----------------------
  //
  // Список начала строился из голой сетки рабочего дня и о занятости не знал:
  // человек выбирал 10:00, нажимал «Забронировать» и только тогда узнавал,
  // что комната занята. Отказ приходил уже после заполнения всей формы —
  // с темой, участниками и повторением.
  //
  // Своя сетка комнаты нужна ещё и потому, что часы работы у комнат разные:
  // policy.work_start — это значение по умолчанию, а не общий закон. Пока
  // ответ не пришёл, показываем сетку по общим правилам: пустой список
  // выглядел бы поломкой, а лишний вариант отсеет сервер.
  const daySlots = useSlots(roomId, date)
  const gridStart = daySlots.data?.work_start ?? policy.work_start
  const gridEnd = daySlots.data?.work_end ?? policy.work_end
  const startStates = useMemo(
    () => startAvailability(daySlots.data?.slots, duration, editingId),
    [daySlots.data, duration, editingId],
  )

  const horizonExceeded = daysFromToday(date) > policy.horizon_days
  const durationExceeded = duration > policy.max_duration_minutes
  const isSeriesBooking = Boolean(existing.data?.recurrence_id)

  // --- Экран 2.14: людей больше, чем мест ----------------------------------
  const headcount = participants.length + 1 // организатор тоже занимает стул
  const overCapacity = Boolean(room && headcount > room.capacity)
  const biggerRooms = useAvailability(startsAt, endsAt, headcount, overCapacity)

  // --- Сохранение -----------------------------------------------------------
  const create = useCreateBooking()
  const update = useUpdateBooking()
  const saveParticipants = useSaveParticipants()

  const saving = create.isPending || update.isPending || saveParticipants.isPending
  const error = create.error ?? update.error ?? saveParticipants.error

  const seriesConflict = isSeriesConflict(error)

  function refs(): ParticipantRef[] {
    return participants.map((participant) =>
      participant.user_id ? { user_id: participant.user_id } : { email: participant.email },
    )
  }

  function submit(withSkipConflicts = skipConflicts) {
    if (!roomId) return

    if (isEditing && editingId !== undefined) {
      update.mutate(
        {
          id: editingId,
          room_id: roomId,
          title: title.trim(),
          starts_at: startsAt,
          ends_at: endsAt,
          apply: isSeriesBooking ? apply : undefined,
        },
        {
          onSuccess: () => {
            // Список участников — отдельный запрос: сервер шлёт добавленным приглашение,
            // а убранным короткое письмо «встреча без вас», иначе человек придёт зря.
            saveParticipants.mutate(
              { bookingId: editingId, participants: refs() },
              { onSuccess: () => navigate('/meetings') },
            )
          },
        },
      )
      return
    }

    create.mutate(
      {
        room_id: roomId,
        title: title.trim(),
        starts_at: startsAt,
        ends_at: endsAt,
        participants: refs(),
        recurrence:
          recurring && until
            ? { rrule: `FREQ=WEEKLY;BYDAY=${WEEKDAY_CODES[atWallTime(date, startTime).getDay()]}`, until }
            : null,
        skip_conflicts: withSkipConflicts,
      },
      {
        // Экран 3.4 «Готово». Признак «позвали по адресу» есть только здесь: в ответе
        // сервера участник, заведённый минуту назад, ничем не отличается от коллеги,
        // который в системе давно, — а человека интересует именно эта разница.
        onSuccess: (created) =>
          navigate(`/booked/${created.id}`, {
            replace: true,
            state: {
              done: {
                invitedByEmail: participants
                  .filter((participant) => participant.pending)
                  .map((participant) => participant.email),
                // warning и skipped живут только в ответе на создание: в самой брони
                // их уже не прочитать. Дальше «Готово» они не идут никуда, поэтому
                // либо показать там, либо потерять.
                warning: created.warning,
                skipped: created.skipped,
              } satisfies BookingDoneHint,
            },
          }),
      },
    )
  }

  /** Альтернатива из ошибки «слот только что заняли» подставляется прямо в форму. */
  function applySuggestion(suggestion: Suggestion) {
    setRoomId(suggestion.room_id)
    setDate(dateKey(parse(suggestion.starts_at)))
    setStartTime(hhmm(parse(suggestion.starts_at)))
    setDuration(minutesBetween(suggestion.starts_at, suggestion.ends_at))
    create.reset()
    update.reset()
  }

  if (isEditing && existing.isLoading) return <Loading text="Открываем встречу…" />

  if (!canBook) {
    return (
      <div className="card p-6">
        <p className="font-medium">Бронирование недоступно</p>
        <p className="mt-2 text-sm text-muted">
          Ваш адрес в чёрном списке: смотреть расписание можно, создавать встречи — нет.
          Снять ограничение может администратор системы.
        </p>
      </div>
    )
  }

  const ready = Boolean(roomId) && !horizonExceeded && !durationExceeded && (!recurring || Boolean(until))

  return (
    <>
      <div className="mb-4">
        <Link to="/" className="text-sm text-muted hover:text-fg">
          ← К расписанию
        </Link>
      </div>

      <PageTitle>{isEditing ? 'Изменить встречу' : 'Новая бронь'}</PageTitle>

      <form
        className="grid gap-5 lg:grid-cols-[1fr_320px]"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) submit()
        }}
      >
        <div className="flex flex-col gap-5">
          <div className="card grid gap-4 p-4 sm:grid-cols-2">
            <Field label="Переговорная" className="sm:col-span-2">
              <Select value={roomId} onChange={(event) => setRoomId(Number(event.target.value))}>
                <option value={0} disabled>
                  Выберите комнату
                </option>
                {(rooms.data ?? []).map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name} — {seats(candidate.capacity)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Дата"
              error={
                horizonExceeded
                  ? `Бронировать можно не дальше чем на ${policy.horizon_days} дней вперёд. Выберите дату поближе.`
                  : undefined
              }
            >
              <Input
                type="date"
                className="tnum"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Field>

            <Field
              label="Начало"
              hint={
                startStates.get(startTime) && startStates.get(startTime) !== 'free'
                  ? undefined
                  : 'Занятое время видно прямо в списке.'
              }
              error={startNote(startStates.get(startTime))}
            >
              <Select value={startTime} onChange={(event) => setStartTime(event.target.value)}>
                {timeOptions(gridStart, gridEnd, startTime).map((option) => {
                  // Занятое не прячем, а показываем недоступным с причиной.
                  // Пропавший из списка вариант человек ищет глазами и решает,
                  // что система сломалась; «10:00 — занято» отвечает на вопрос
                  // сразу и заодно говорит, что комната в это время используется.
                  const state = startStates.get(option) ?? 'free'
                  return (
                    <option key={option} value={option} disabled={state !== 'free'}>
                      {option}
                      {state === 'busy' && ' — занято'}
                      {state === 'tooLong' && ` — не помещается ${fmtDuration(duration)}`}
                      {state === 'past' && ' — прошло'}
                    </option>
                  )
                })}
              </Select>
            </Field>

            <Field
              label="Длительность"
              error={
                durationExceeded
                  ? `Максимум ${fmtDuration(policy.max_duration_minutes)} на встречу — так решено в правилах.`
                  : undefined
              }
            >
              <Select value={duration} onChange={(event) => setDuration(Number(event.target.value))}>
                {durationOptions(policy.max_duration_minutes, duration).map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {fmtDuration(minutes)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Тема встречи"
              hint="Её видят все — это ответ на вопрос «кто внутри» на планшете у двери."
              className="sm:col-span-2"
            >
              <Input
                value={title}
                maxLength={200}
                placeholder="Например: защита квартального бюджета"
                onChange={(event) => setTitle(event.target.value)}
              />
            </Field>
          </div>

          <div className="card p-4">
            <p className="label">Участники</p>
            <ParticipantPicker value={participants} onChange={setParticipants} />

            {/* Экран 2.14 — предупреждение, а не запрет: часть людей подключается удалённо. */}
            {overCapacity && room && (
              <div className="mt-3 rounded-lg border border-state-soon/40 bg-state-soon/10 px-3 py-2 text-sm">
                <p className="font-medium">
                  В «{room.name}» {seats(room.capacity)}, а с вами набирается {people(headcount)}.
                </p>
                <p className="mt-1 text-fg/80">
                  Это не запрет — часть приглашённых может подключиться удалённо. Если нужны все
                  вживую, вот комнаты подходящего размера на это же время:
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(biggerRooms.data?.rooms ?? [])
                    .filter((row) => row.available && row.room.id !== room.id)
                    .map((row) => (
                      <Button
                        key={row.room.id}
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setRoomId(row.room.id)}
                      >
                        {row.room.name} — {seats(row.room.capacity)}
                      </Button>
                    ))}
                  {(biggerRooms.data?.rooms ?? []).filter((row) => row.available).length === 0 && (
                    <span className="text-muted">Свободных комнат побольше на это время нет.</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Право на повторы выдаётся персонально: у кого его нет, тот поля не видит. */}
          {user.can_recur && !isEditing && (
            <div className="card p-4">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={recurring}
                  onChange={(event) => setRecurring(event.target.checked)}
                />
                <span className="font-medium">Повторять каждую неделю</span>
              </label>

              {recurring && (
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Field label="По каким дням" group>
                    <p className="field bg-surface-2">
                      По {WEEKDAY_NAMES[atWallTime(date, startTime).getDay()]}
                    </p>
                  </Field>
                  <Field
                    label="До какой даты"
                    hint={
                      until
                        ? // Скобочная плюрализация «встреч(и)» — это признание, что число
                          // посчитали, а согласовать поленились; в проекте для этого есть words.
                          `${meetings(weeklyCount(date, until))}. Дальше горизонта система создаст их сама, по мере приближения.`
                        : 'Серия хранится правилом, а брони появляются скользящим окном — горизонт ей не мешает.'
                    }
                  >
                    <Input
                      type="date"
                      className="tnum"
                      value={until}
                      min={date}
                      onChange={(event) => setUntil(event.target.value)}
                    />
                  </Field>
                </div>
              )}
            </div>
          )}

          {/* Экран 2.10: правка брони из серии — обязательный явный выбор. */}
          {isEditing && isSeriesBooking && (
            <div className="card p-4">
              <p className="label">Эта встреча из повторяющейся серии</p>
              <div className="flex flex-col gap-2">
                <ApplyOption
                  name="apply-scope"
                  checked={apply === 'single'}
                  onChange={() => setApply('single')}
                  label="Изменить только эту встречу"
                  hint={`${fmtDate(startsAt)}, ${fmtRange(startsAt, endsAt)}`}
                />
                <ApplyOption
                  name="apply-scope"
                  checked={apply === 'series'}
                  onChange={() => setApply('series')}
                  label="Изменить всю серию"
                  hint={existing.data?.recurrence?.human_readable ?? 'Все будущие встречи серии'}
                />
              </div>
            </div>
          )}

          {error && (
            <ErrorNotice
              error={error}
              onPickSuggestion={applySuggestion}
            />
          )}

          {/* Экран 2.11: часть дат серии занята — предлагаем пропустить их, а не всё отменять. */}
          {seriesConflict && (
            <div className="rounded-card border border-state-soon/40 bg-state-soon/10 px-4 py-3">
              <p className="text-sm font-medium">Часть дат серии занята</p>
              <p className="mt-1 text-sm text-fg/80">
                Можно создать встречи на свободные даты, а занятые пропустить — остальная серия
                при этом не пострадает.
              </p>
              <Button
                type="button"
                size="sm"
                variant="primary"
                className="mt-3"
                loading={saving}
                onClick={() => {
                  setSkipConflicts(true)
                  submit(true)
                }}
              >
                Пропустить занятые даты
              </Button>
            </div>
          )}
        </div>

        {/* Сводка справа: что именно будет создано. */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="card flex flex-col gap-3 p-4">
            <p className="font-medium">{title.trim() || 'Без темы'}</p>
            <p className="tnum text-sm text-muted">
              {room?.name ?? 'Комната не выбрана'}
              <br />
              {fmtDate(startsAt)}, {fmtRange(startsAt, endsAt)}
              <br />
              {fmtDuration(duration)}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Badge>{invited(participants.length)}</Badge>
              {room && <Badge>{seats(room.capacity)}</Badge>}
              {recurring && until && <Badge tone="accent">Серия до {fmtDate(until)}</Badge>}
            </div>

            <Button type="submit" variant="primary" size="lg" disabled={!ready} loading={saving}>
              {isEditing
                ? 'Сохранить изменения'
                : participants.length > 0
                  ? 'Забронировать и позвать'
                  : 'Забронировать'}
            </Button>

            <p className="hint">
              {isEditing
                ? 'Участники получат письмо с прежним и новым временем рядом.'
                : 'Приглашения уходят на почту с файлом календаря. Письмо ставится в очередь в той же транзакции, что и бронь.'}
            </p>
          </div>
        </aside>
      </form>
    </>
  )
}

function ApplyOption({
  checked,
  onChange,
  label,
  hint,
  name,
}: {
  checked: boolean
  onChange: () => void
  label: string
  hint?: string
  /** Общее имя переключателей развилки: без него стрелки на клавиатуре не работают. */
  name: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition',
        checked ? 'border-accent bg-accent-soft/40' : 'border-line hover:bg-surface-2',
      )}
    >
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1" />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="tnum block text-sm text-muted">{hint}</span>}
      </span>
    </label>
  )
}

/**
 * Конфликт дат серии приходит тем же кодом slot_taken, но с деталями по датам
 * и без готовых альтернатив: предлагать другую комнату для двадцати встреч бессмысленно,
 * а пропустить занятые даты — осмысленно.
 */
function isSeriesConflict(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.code !== 'slot_taken') return false
  return Boolean(error.details && Object.keys(error.details).length > 0) && !error.suggestions?.length
}

function initialDate(startParam: string | null): string {
  return startParam ? dateKey(parse(startParam)) : dateKey(new Date())
}

function initialTime(startParam: string | null, workStart: string): string {
  return startParam ? hhmm(parse(startParam)) : workStart
}

function initialDuration(startParam: string | null, endParam: string | null): number {
  if (!startParam || !endParam) return DEFAULT_DURATION_MINUTES
  const minutes = minutesBetween(startParam, endParam)
  return minutes > 0 ? minutes : DEFAULT_DURATION_MINUTES
}

function hhmm(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Что не так с этим временем начала. free — всё в порядке. */
type StartState = 'free' | 'busy' | 'tooLong' | 'past'

/**
 * Разметка времён начала по занятости комнаты.
 *
 * Считается по той же сетке слотов, что показывает страница комнаты, — то есть
 * по ответу сервера, а не по собственному представлению формы о том, что занято.
 * Второй источник правды здесь означал бы, что форма разрешает то, что сервер
 * потом отклонит, и наоборот.
 *
 * Различаем «занято» и «не помещается» намеренно. Начало в 10:00 может быть
 * свободно, а встреча на два часа — упираться в чужую бронь в 11:00. Сказать
 * в обоих случаях «занято» значит соврать: человек видит свободные 10:00
 * на таймлайне и не понимает, почему форма их не берёт. Правильный ответ —
 * «сократите встречу», и он должен быть виден без догадок.
 *
 * При правке своей встречи её собственные слоты не считаются занятыми: иначе
 * нельзя было бы просто поменять тему, не сдвинув время.
 */
function startAvailability(
  slots: Slot[] | undefined,
  durationMinutes: number,
  editingId: number | undefined,
): Map<string, StartState> {
  const states = new Map<string, StartState>()
  if (!slots) return states

  const free = new Map<number, boolean>()
  const past = new Map<number, boolean>()
  for (const slot of slots) {
    const at = new Date(slot.starts_at).getTime()
    // Своя же бронь не мешает себе: при правке она стоит в сетке занятой.
    const mine = editingId !== undefined && slot.booking_id === editingId
    free.set(at, slot.free || mine)
    past.set(at, slot.past)
  }

  const need = Math.max(1, Math.ceil(durationMinutes / SLOT_MINUTES))
  for (const [at, isFree] of free) {
    const label = hhmm(new Date(at))
    if (past.get(at)) {
      states.set(label, 'past')
      continue
    }
    if (!isFree) {
      states.set(label, 'busy')
      continue
    }
    // Встреча занимает несколько получасов подряд: свободного начала мало,
    // нужен свободный хвост. Выход за конец сетки — тоже «не помещается»:
    // дальше комната не работает.
    let fits = true
    for (let step = 1; step < need; step += 1) {
      const next = at + step * SLOT_MINUTES * 60_000
      if (!free.has(next) || !free.get(next)) {
        fits = false
        break
      }
    }
    states.set(label, fits ? 'free' : 'tooLong')
  }
  return states
}

/** Объяснение под полем, когда выбранное время взять нельзя. */
function startNote(state: StartState | undefined): string | undefined {
  if (state === 'busy') return 'В это время переговорная занята. Выберите другое время или комнату.'
  if (state === 'tooLong') return 'Встреча такой длины сюда не помещается — до следующей брони меньше времени.'
  if (state === 'past') return 'Это время уже прошло.'
  return undefined
}

/**
 * Список времён рабочего дня плюс текущее значение, если оно из него выпадает:
 * бронь «с 13:45» пришла с таймлайна законно, и терять её при открытии формы нельзя.
 */
function timeOptions(workStart: string, workEnd: string, current: string): string[] {
  const options = wallTimeOptions(workStart, workEnd)
  return options.includes(current) ? options : [current, ...options].sort()
}

function durationOptions(maxMinutes: number, current: number): number[] {
  const options: number[] = []
  for (let minutes = 30; minutes <= Math.max(30, maxMinutes); minutes += 30) options.push(minutes)
  if (!options.includes(current)) options.push(current)
  return options.sort((left, right) => left - right)
}

/** Сколько встреч получится в еженедельной серии — показываем до сохранения. */
function weeklyCount(from: string, until: string): number {
  const start = parse(from).getTime()
  const end = parse(until).getTime()
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0
  return Math.floor((end - start) / (7 * 86_400_000)) + 1
}
